import { EVENT_SIMULATION_NOTE } from '@fos/shared/contracts/inbox';
import { SEVERITY_LABELS } from '@fos/shared/labels';
import { useEffect, useState } from 'react';
import { errorMessage, validationIssues } from '../../../lib/api';
import { trpc } from '../../../lib/trpc';
import { ContextBanner, FormMessage } from '../../../ui/Blocks';
import { Button } from '../../../ui/Button';
import { Dialog } from '../../../ui/Dialog';
import { Field } from '../../../ui/Field';
import { Input, Textarea } from '../../../ui/Input';
import { toast } from '../../../ui/Toast';
import { useMailRefresh } from '../hooks';

/**
 * Narzędzie dev „Symuluj przychodzącą wiadomość” (tylko development, właściciel / admin; `mail.status.canSimulate`):
 * mail trafia do skrzynki w symulacji i przechodzi tę samą drogę co z synchronizacji (wątek, dopasowanie klienta,
 * klasyfikacja, Inbox), oznaczony jako symulacja.
 */
export function SimulateIncomingDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated?: (threadId: string) => void;
}) {
  const refresh = useMailRefresh();
  const [fromEmail, setFromEmail] = useState('anna@rea.pl');
  const [fromName, setFromName] = useState('Anna Kowalczyk');
  const [subject, setSubject] = useState('Sekcja FAQ na stronie');
  const [body, setBody] = useState('Dzień dobry,\n\nczy możecie dodać na stronie sekcję FAQ?\n\nAnna');
  const simulate = trpc.mail.simulateIncoming.useMutation({
    onSuccess: async (res) => {
      await refresh();
      toast.success(`Symulacja: wiadomość dodana (${SEVERITY_LABELS[res.severity]})`);
      onCreated?.(res.threadId);
      onOpenChange(false);
    },
  });
  // biome-ignore lint/correctness/useExhaustiveDependencies: tylko przy otwarciu
  useEffect(() => {
    if (open) simulate.reset();
  }, [open]);

  const issues = simulate.isError ? validationIssues(simulate.error) : [];
  const issue = (path: string) => issues.find((i) => i.path.startsWith(path))?.message ?? null;
  const submit = () =>
    simulate.mutate({
      from: { email: fromEmail.trim(), ...(fromName.trim() ? { name: fromName.trim() } : {}) },
      subject: subject.trim(),
      bodyText: body,
    });

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Symuluj przychodzącą wiadomość"
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Anuluj
          </Button>
          <Button variant="primary" loading={simulate.isPending} onClick={submit}>
            Symuluj
          </Button>
        </>
      }
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <ContextBanner tone="info">{EVENT_SIMULATION_NOTE}. Tylko środowisko deweloperskie.</ContextBanner>
        {simulate.isError && issues.length === 0 && (
          <FormMessage>{errorMessage(simulate.error, 'Nie udało się zasymulować wiadomości.')}</FormMessage>
        )}
        <div className="grid gap-4 md:grid-cols-2">
          <Field label="Adres nadawcy" error={issue('from.email')}>
            <Input value={fromEmail} onChange={(e) => setFromEmail(e.target.value)} inputMode="email" />
          </Field>
          <Field label="Nadawca">
            <Input value={fromName} onChange={(e) => setFromName(e.target.value)} />
          </Field>
        </div>
        <Field label="Temat" error={issue('subject')}>
          <Input value={subject} onChange={(e) => setSubject(e.target.value)} />
        </Field>
        <Field label="Treść" error={issue('bodyText')}>
          <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={6} />
        </Field>
      </form>
    </Dialog>
  );
}
